import React from 'react';
import SEO from '../components/SEO';

const UIFullStackReact: React.FC = () => {
  return (
    <>
      <SEO 
        title="UI Full Stack Web Development with React Training | React Course - SBRIT Solution"
        description="Complete full-stack web development with React frontend and modern backend. Build modern web applications with React and full-stack technologies."
        keywords="ui full stack react training, react web development, full stack development, react frontend, modern web development, react course, javascript development"
        url="https://sbritsolution.in/courses/ui-full-stack-react"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                UI Full Stack Web Development with React
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Complete Full-Stack Development | React Frontend | Modern Backend | Database Integration | API Development
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Start Learning - ₹29,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Why UI Full Stack React */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Learn UI Full Stack React?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚛️</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Popular Framework</h3>
                <p className="text-gray-600">React is the most popular frontend framework used by major companies worldwide.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💼</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">High Demand</h3>
                <p className="text-gray-600">Full-stack React developers are in extremely high demand with excellent salary packages.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Complete Development</h3>
                <p className="text-gray-600">Learn to build complete web applications from frontend to backend with modern technologies.</p>
              </div>
            </div>
          </div>
        </div>

        {/* UI Full Stack React Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete UI Full Stack React Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">React Fundamentals</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• React Introduction & Setup</li>
                    <li>• JSX & Components</li>
                    <li>• Props & State Management</li>
                    <li>• Lifecycle Methods</li>
                    <li>• Event Handling</li>
                    <li>• Conditional Rendering</li>
                    <li>• Lists & Keys</li>
                    <li>• Forms & Controlled Components</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-cyan-600">Advanced React</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• React Hooks (useState, useEffect)</li>
                    <li>• Custom Hooks</li>
                    <li>• Context API</li>
                    <li>• React Router</li>
                    <li>• Error Boundaries</li>
                    <li>• Performance Optimization</li>
                    <li>• React Testing</li>
                    <li>• TypeScript with React</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-purple-600">State Management</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Redux Fundamentals</li>
                    <li>• Redux Toolkit</li>
                    <li>• Redux Thunk & Saga</li>
                    <li>• Zustand State Management</li>
                    <li>• React Query</li>
                    <li>• State Persistence</li>
                    <li>• DevTools & Debugging</li>
                    <li>• Best Practices</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">Backend Development</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Node.js Fundamentals</li>
                    <li>• Express.js Framework</li>
                    <li>• RESTful API Development</li>
                    <li>• Database Integration</li>
                    <li>• Authentication & Authorization</li>
                    <li>• File Upload & Storage</li>
                    <li>• API Testing</li>
                    <li>• Deployment & Production</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tools & Technologies */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Tools & Technologies You'll Master</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚛️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">React</h3>
                <p className="text-gray-600 text-sm">Modern UI library for building user interfaces</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🟦</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">TypeScript</h3>
                <p className="text-gray-600 text-sm">Type-safe JavaScript for better development</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Node.js</h3>
                <p className="text-gray-600 text-sm">JavaScript runtime for backend development</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗄️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">MongoDB</h3>
                <p className="text-gray-600 text-sm">NoSQL database for modern applications</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Features */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">What You'll Get</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📚</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Comprehensive Content</h3>
                <p className="text-gray-600 text-sm">100+ hours of structured learning with hands-on projects</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-cyan-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">💻</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Real Projects</h3>
                <p className="text-gray-600 text-sm">Build complete full-stack applications from scratch</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">🎓</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Certification</h3>
                <p className="text-gray-600 text-sm">Industry-recognized certificate upon completion</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
                <p className="text-gray-600 text-sm">24/7 support from React development experts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-blue-600">React Developer</h3>
                <p className="text-gray-600 mb-4">Build modern user interfaces with React</p>
                <div className="text-sm text-gray-500">Average Salary: ₹6-12 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Full Stack Developer</h3>
                <p className="text-gray-600 mb-4">Develop complete web applications from frontend to backend</p>
                <div className="text-sm text-gray-500">Average Salary: ₹8-16 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Frontend Engineer</h3>
                <p className="text-gray-600 mb-4">Specialize in modern frontend technologies and user experience</p>
                <div className="text-sm text-gray-500">Average Salary: ₹7-14 LPA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Build Modern Web Applications?</h2>
            <p className="text-xl mb-8">Join thousands of developers building amazing applications with React</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹29,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Download Course Brochure
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UIFullStackReact;
